import { apiService } from './apiService'
import { buildApiUrl } from '@utils/appDetection'

class AuthService {
  // Token management
  getTokenKey(appType) {
    switch (appType) {
      case 'super-admin':
        return 'super_admin_token'
      case 'tenant':
        const path = window.location.pathname
        if (path.startsWith('/admin')) return 'tenant_admin_token'
        if (path.startsWith('/state-official')) return 'state_official_token'
        return 'tenant_token'
      default:
        return 'auth_token'
    }
  }

  getToken(appType) {
    const tokenKey = this.getTokenKey(appType)
    return localStorage.getItem(tokenKey)
  }

  setToken(token, appType) {
    const tokenKey = this.getTokenKey(appType)
    localStorage.setItem(tokenKey, token)
  }

  removeToken(appType) {
    const tokenKey = this.getTokenKey(appType)
    localStorage.removeItem(tokenKey)
  }

  // Authentication methods
  async login(credentials, appType) {
    let endpoint
    
    switch (appType) {
      case 'super-admin':
        endpoint = '/super-admin/login'
        break
      case 'tenant':
        const path = window.location.pathname
        if (path.startsWith('/admin')) {
          endpoint = '/client-admin/login'
        } else if (path.startsWith('/state-official')) {
          endpoint = '/state-official/login'
        } else {
          throw new Error('Invalid login context')
        }
        break
      default:
        throw new Error('Invalid app type')
    }

    const response = await apiService.post(endpoint, credentials)
    return response.data
  }

  async logout(appType) {
    let endpoint
    
    switch (appType) {
      case 'super-admin':
        endpoint = '/super-admin/logout'
        break
      case 'tenant':
        const path = window.location.pathname
        if (path.startsWith('/admin')) {
          endpoint = '/client-admin/logout'
        } else if (path.startsWith('/state-official')) {
          endpoint = '/state-official/logout'
        }
        break
    }

    if (endpoint) {
      try {
        await apiService.post(endpoint)
      } catch (error) {
        console.warn('Logout request failed:', error)
      }
    }
  }

  async getCurrentUser(appType) {
    let endpoint
    
    switch (appType) {
      case 'super-admin':
        endpoint = '/super-admin/me'
        break
      case 'tenant':
        const path = window.location.pathname
        if (path.startsWith('/admin')) {
          endpoint = '/client-admin/me'
        } else if (path.startsWith('/state-official')) {
          endpoint = '/state-official/me'
        }
        break
    }

    if (!endpoint) {
      throw new Error('Cannot determine user endpoint')
    }

    const response = await apiService.get(endpoint)
    return response.data
  }

  async refreshToken(appType) {
    let endpoint
    
    switch (appType) {
      case 'super-admin':
        endpoint = '/super-admin/refresh'
        break
      case 'tenant':
        const path = window.location.pathname
        if (path.startsWith('/admin')) {
          endpoint = '/client-admin/refresh'
        } else if (path.startsWith('/state-official')) {
          endpoint = '/state-official/refresh'
        }
        break
    }

    if (!endpoint) {
      throw new Error('Cannot determine refresh endpoint')
    }

    const response = await apiService.post(endpoint)
    return response.data
  }

  // State Official specific methods
  async changePassword(data) {
    const response = await apiService.post('/state-official/change-password', data)
    return response.data
  }

  async completeSetupStep1(data) {
    const response = await apiService.post('/state-official/complete-setup-step-1', data)
    return response.data
  }

  async completeSetupStep2(data) {
    const response = await apiService.post('/state-official/complete-setup-step-2', data)
    return response.data
  }

  // Utility methods
  isAuthenticated(appType) {
    const token = this.getToken(appType)
    return !!token
  }

  decodeToken(token) {
    try {
      const payload = token.split('.')[1]
      const decoded = JSON.parse(atob(payload))
      return decoded
    } catch (error) {
      console.error('Failed to decode token:', error)
      return null
    }
  }

  isTokenExpired(token) {
    const decoded = this.decodeToken(token)
    if (!decoded || !decoded.exp) {
      return true
    }
    
    const currentTime = Math.floor(Date.now() / 1000)
    return decoded.exp < currentTime
  }

  getAuthHeaders(appType) {
    const token = this.getToken(appType)
    return token ? { Authorization: `Bearer ${token}` } : {}
  }
}

export const authService = new AuthService()
export default authService